import React, { useState, useEffect } from 'react';
import { Trade } from '../types';
import { IconOps, IconSearch } from '../components/Icons';
import TradeTicket from '../components/TradeTicket';
import toast from 'react-hot-toast';
import { useAiQ } from '../state/AiQState'; // Import Global State

interface MultiAssetOpsViewProps {
    initialTicketData?: any;
    onClearTicketData?: () => void;
}

const MultiAssetOpsView: React.FC<MultiAssetOpsViewProps> = ({ initialTicketData, onClearTicketData }) => {
  // FIX: Use trades from Context, NOT mockTrades
  const { trades, addTrade, updateTradeStatus } = useAiQ(); 
  
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);
  const [filter, setFilter] = useState('');
  const [isTicketOpen, setIsTicketOpen] = useState(false);
  const [processingAction, setProcessingAction] = useState<string | null>(null);

  // Auto-open ticket if data passed from Risk View
  useEffect(() => {
      if (initialTicketData) {
          setIsTicketOpen(true);
      }
  }, [initialTicketData]);

  const handleTicketClose = () => {
      setIsTicketOpen(false);
      if (onClearTicketData) onClearTicketData();
  };

  // Filter the REAL trades
  const filteredTrades = trades.filter(t => 
    t.id.toLowerCase().includes(filter.toLowerCase()) || 
    t.product.toLowerCase().includes(filter.toLowerCase()) ||
    t.counterparty.toLowerCase().includes(filter.toLowerCase())
  );

  const handleNewTrade = (newTrade: any) => {
      addTrade(newTrade); // Commit to Global Context
      setSelectedTrade(newTrade);
  };

  // Button Action Handlers
  const updateStatus = async (status: Trade['status'], actionName: string) => {
      if (!selectedTrade) return;
      setProcessingAction(actionName);
      
      // Simulate API Call
      await new Promise(r => setTimeout(r, 1000));
      
      updateTradeStatus(selectedTrade.id, status);
      setSelectedTrade(prev => prev ? { ...prev, status } : null);
      
      setProcessingAction(null);
      toast.success(`Trade ${selectedTrade.id} ${status}`);
  };

  const handleAmend = () => {
      toast("Amendment Request Sent to Back Office", { icon: '📝' });
  };

  const getStatusColor = (status: Trade['status']) => {
    switch(status) {
      case 'Settled': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Verified': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Dispute': return 'bg-red-100 text-red-700 border-red-200';
      case 'Matured': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  return (
    <div className="flex h-full">
      <TradeTicket 
        isOpen={isTicketOpen} 
        onClose={handleTicketClose} 
        onBook={handleNewTrade}
        initialData={initialTicketData}
      />

      {/* Left Panel: Trade Blotter */}
      <div className="flex-1 flex flex-col border-r border-slate-200 bg-white">
        <div className="p-6 border-b border-slate-100">
           <div className="flex justify-between items-center mb-4">
             <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
               <span className="text-blue-600"><IconOps /></span>
               Global Trade Blotter
             </h2>
             <div className="flex gap-2">
               <button className="px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded hover:bg-slate-200">Export CSV</button>
               <button 
                 onClick={() => setIsTicketOpen(true)}
                 className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm"
               >
                 + New Trade
               </button>
             </div>
           </div>
           <div className="relative">
             <input 
               type="text" 
               placeholder="Search Trade ID, Product, Counterparty..." 
               value={filter}
               onChange={(e) => setFilter(e.target.value)}
               className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
             />
             <span className="absolute left-3 top-2.5 text-slate-400"><IconSearch /></span>
           </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
          {filteredTrades.length > 0 ? (
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-500 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 font-medium rounded-l-lg">Trade ID</th>
                  <th className="px-4 py-3 font-medium">Product</th>
                  <th className="px-4 py-3 font-medium">Counterparty</th>
                  <th className="px-4 py-3 font-medium text-right">Notional</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium rounded-r-lg">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredTrades.map((trade) => (
                  <tr 
                    key={trade.id} 
                    onClick={() => setSelectedTrade(trade)}
                    className={`cursor-pointer transition-colors ${selectedTrade?.id === trade.id ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                  >
                    <td className="px-4 py-3 font-mono text-slate-600">{trade.id}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{trade.product}</td>
                    <td className="px-4 py-3 text-slate-600">{trade.counterparty}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">{trade.currency} {(trade.notional/1000).toFixed(1)}k</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${getStatusColor(trade.status)}`}>
                        {trade.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{trade.tradeDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
             <div className="flex items-center justify-center h-64 text-slate-400 text-sm italic">
                No trades found. Add a transaction in the Main App to see it here.
             </div>
          )}
        </div>
      </div>

      {/* Right Panel: Trade Detail & Lifecycle */}
      <div className="w-96 bg-slate-50 border-l border-slate-200 flex flex-col overflow-y-auto custom-scrollbar">
        {selectedTrade ? (
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-bold text-slate-800">{selectedTrade.id}</h3>
              <p className="text-slate-500 text-sm">{selectedTrade.product}</p>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2">
               <button 
                 onClick={handleAmend}
                 className="flex-1 py-2 bg-white border border-slate-200 shadow-sm rounded text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
               >
                 Amend
               </button>
               <button 
                 onClick={() => updateStatus('Matured', 'cancel')}
                 disabled={processingAction !== null}
                 className="flex-1 py-2 bg-white border border-slate-200 shadow-sm rounded text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
               >
                 {processingAction === 'cancel' ? '...' : 'Cancel'}
               </button>
               <button 
                 onClick={() => updateStatus('Verified', 'verify')}
                 disabled={processingAction !== null || selectedTrade.status === 'Verified'}
                 className="flex-1 py-2 bg-blue-600 shadow-sm rounded text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:bg-slate-400"
               >
                 {processingAction === 'verify' ? '...' : 'Verify'}
               </button>
            </div>

            {/* Details Grid */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 space-y-3">
               <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Trade Details</h4>
               <div className="flex justify-between text-sm">
                 <span className="text-slate-500">Trader</span>
                 <span className="font-medium text-slate-800">{selectedTrade.trader}</span>
               </div>
               <div className="flex justify-between text-sm">
                 <span className="text-slate-500">Counterparty</span>
                 <span className="font-medium text-slate-800">{selectedTrade.counterparty}</span>
               </div>
               <div className="flex justify-between text-sm">
                 <span className="text-slate-500">Settlement Date</span>
                 <span className="font-medium text-slate-800">{selectedTrade.settlementDate}</span>
               </div>
               <div className="flex justify-between text-sm">
                 <span className="text-slate-500">Current Valuation</span>
                 <span className={`font-medium ${selectedTrade.valuation >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                   {selectedTrade.currency} {selectedTrade.valuation.toLocaleString()}
                 </span>
               </div>
            </div>

            {/* Lifecycle Visualizer */}
            <div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Trade Lifecycle</h4>
              <div className="relative border-l-2 border-slate-200 ml-3 space-y-8 py-2">
                {/* Step 1 */}
                <div className="relative pl-6">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-blue-500 border-2 border-white ring-1 ring-slate-200"></div>
                  <p className="text-xs text-slate-400">{selectedTrade.tradeDate} 09:45:00</p>
                  <p className="text-sm font-medium text-slate-800">Trade Captured</p>
                  <p className="text-xs text-slate-500">Source: FIX Gateway</p>
                </div>
                {/* Step 2 */}
                <div className="relative pl-6">
                  <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ring-1 ring-slate-200 ${['Pending'].includes(selectedTrade.status) ? 'bg-amber-400 animate-pulse' : 'bg-blue-500'}`}></div>
                  <p className="text-xs text-slate-400">{selectedTrade.tradeDate} 09:45:05</p>
                  <p className="text-sm font-medium text-slate-800">Validation & Enrichment</p>
                  <p className="text-xs text-slate-500">Limits check passed.</p>
                </div>
                {/* Step 3 */}
                <div className="relative pl-6">
                  <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ring-1 ring-slate-200 ${['Dispute'].includes(selectedTrade.status) ? 'bg-red-500' : ['Verified', 'Settled'].includes(selectedTrade.status) ? 'bg-blue-500' : 'bg-slate-200'}`}></div>
                  <p className="text-xs text-slate-400">--</p>
                  <p className="text-sm font-medium text-slate-800">Confirmation Matched</p>
                  {selectedTrade.status === 'Dispute' && <p className="text-xs text-red-500 font-semibold">Mismatch on Notional</p>}
                </div>
                 {/* Step 4 */}
                 <div className="relative pl-6">
                  <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ring-1 ring-slate-200 ${selectedTrade.status === 'Settled' ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>
                  <p className="text-xs text-slate-400">{selectedTrade.settlementDate}</p>
                  <p className="text-sm font-medium text-slate-800">Settlement</p>
                </div>
              </div>
            </div>

          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-10 text-center">
             <div className="opacity-20 mb-4"><IconOps /></div>
             <p>Select a trade to view details, lifecycle events, and settlement status.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiAssetOpsView;